import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import SimpleBar from 'simplebar-react'

import useCall from '~/hooks/useCall'

const SkipTicket = () => {
  const { isQueueStarted, callData, selectQueue, selectedTicket, currentServing } = useCall()

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [scrollAtStart, setScrollAtStart] = useState(true)
  const [scrollAtEnd, setScrollAtEnd] = useState(false)

  const handlePrev = () => {
    const currentPosition = scrollContainerRef.current?.scrollLeft ?? 0
    const goToPosition = currentPosition - 200
    scrollContainerRef.current?.scrollTo({ left: goToPosition, behavior: 'smooth' })
  }

  const handleNext = () => {
    const currentPosition = scrollContainerRef.current?.scrollLeft ?? 0
    const goToPosition = currentPosition + 200
    scrollContainerRef.current?.scrollTo({ left: goToPosition, behavior: 'smooth' })
  }

  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const currentPosition = Math.ceil(scrollContainerRef.current.scrollLeft)
        const maxScrollPosition = scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth
        setScrollAtStart(currentPosition === 0)
        setScrollAtEnd(currentPosition === maxScrollPosition)
      }
    }

    const currentRef = scrollContainerRef.current

    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll)
    }

    if (!isQueueStarted) {
      setScrollAtStart(true)
      setScrollAtEnd(false)
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll)
      }
    }
  }, [isQueueStarted, callData.skip_list.length])

  return (
    <div className="rounded-lg bg-white p-2.5">
      {isQueueStarted ? (
        <>
          {callData.skip_list.length > 0 ? (
            <div className="text-xl font-bold text-jba-blue">
              <div className="text-xs font-bold text-jba-blue">Skip Ticket</div>
              <div className="relative">
                <button
                  type="button"
                  className="absolute left-0 top-1.5 z-10 inline-flex h-6 w-6 items-center justify-center rounded-full bg-jba-blue text-xl text-white hover:bg-jba-lightBlue disabled:cursor-not-allowed disabled:bg-gray-400"
                  onClick={handlePrev}
                  disabled={scrollAtStart}
                >
                  <Icon icon="material-symbols:chevron-left-rounded" />
                </button>
                {!scrollAtStart && (
                  <div className="absolute left-8 top-0 z-10 inline-block h-14 w-6 bg-gradient-to-r from-white" />
                )}
                <SimpleBar
                  className="mx-8 whitespace-nowrap pb-3"
                  forceVisible="x"
                  autoHide={false}
                  scrollableNodeProps={{ ref: scrollContainerRef }}
                >
                  {callData.skip_list.map((ticket, index) => {
                    const isSelected = selectedTicket.queue_id === ticket.queue_id

                    return (
                      <button
                        type="button"
                        key={index}
                        className={clsx(
                          'relative ml-2 inline-block rounded-lg px-3 py-1.5 text-center text-xs first:ml-0',
                          'before:absolute before:inset-y-0 before:z-10 before:my-auto before:inline-block before:h-3 before:w-1.5 before:rounded-r-full before:bg-white',
                          'after:absolute after:inset-y-0 after:z-10 after:my-auto after:inline-block after:h-3 after:w-1.5 after:rounded-l-full after:bg-white',
                          isSelected && 'pointer-events-none bg-jba-red text-white before:left-0 after:right-0',
                          !isSelected &&
                            !ticket.is_kode_unik &&
                            'bg-jba-blue text-white before:left-0 after:right-0 hover:bg-jba-lightBlue',
                          // Queue taken with a pickup kode unik - same red style as the awaiting card
                          !isSelected &&
                            ticket.is_kode_unik && [
                              'border border-red-300 bg-red-50 text-red-700 hover:bg-red-100',
                              'before:-left-px before:border before:border-l-0 before:border-red-300',
                              'after:-right-px after:border after:border-r-0 after:border-red-300',
                            ],
                        )}
                        disabled={currentServing.isIdle}
                        onClick={() => selectQueue(ticket.queue_id, 'skip')}
                      >
                        {ticket.queue_no}
                      </button>
                    )
                  })}
                </SimpleBar>
                {!scrollAtEnd && (
                  <div className="absolute right-8 top-0 z-10 inline-block h-14 w-6 bg-gradient-to-l from-white" />
                )}
                <button
                  type="button"
                  className="absolute right-0 top-1.5 z-10 inline-flex h-6 w-6 items-center justify-center rounded-full bg-jba-blue text-xl text-white hover:bg-jba-lightBlue disabled:cursor-not-allowed disabled:bg-gray-400"
                  onClick={handleNext}
                  disabled={scrollAtEnd}
                >
                  <Icon icon="material-symbols:chevron-right-rounded" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex h-[3.75rem] items-center justify-center text-gray-400">Belum ada yang dilewati.</div>
          )}
        </>
      ) : (
        <div className="flex h-[3.75rem] items-center justify-center text-gray-400">Antrean belum dimulai.</div>
      )}
    </div>
  )
}

export default SkipTicket
